/**
 * RAF-1 form field definitions grouped by section.
 * Each section maps to a tab in the review form.
 * Fields reference keys in the flat rafExtractedJson object.
 */

export interface FieldDef {
  key: string;
  label: string;
  type?: 'text' | 'checkbox' | 'date' | 'currency';
  /**
   * Optional group identifier — fields sharing a groupId within the same section
   * collapse to ONE logical field for completion counting. Used for
   * mutually-exclusive choices like Yes/No checkbox pairs (`unrepresented_yes`
   * + `unrepresented_no`) or one-of-N selections (`capacity_*`,
   * `claimant_preferred_communication_*`). The group counts as filled if any
   * member field has a value. Group IDs only need to be unique within a section.
   */
  groupId?: string;
}

export interface FieldSection {
  id: string;
  label: string;
  shortLabel: string;
  fields: FieldDef[];
}

const t = (
  key: string,
  label: string,
  type?: FieldDef['type'],
  groupId?: string,
): FieldDef => ({ key, label, type, groupId });

export const RAF_FIELD_SECTIONS: FieldSection[] = [
  {
    id: 'representation',
    label: 'Representation',
    shortLabel: 'Rep',
    fields: [
      t('unrepresented_yes', 'Unrepresented (Yes)', 'checkbox', 'rep_status'),
      t('unrepresented_no', 'Unrepresented (No)', 'checkbox', 'rep_status'),
      t('name_and_surname_of_representative', 'Representative Name & Surname'),
      t('capacity_of_representative', 'Capacity of Representative'),
      t('name_of_firm_of_representative', 'Firm Name'),
      t('representative_id_passport_number', 'Representative ID/Passport'),
    ],
  },
  {
    id: 'banking',
    label: 'Banking Details',
    shortLabel: 'Bank',
    fields: [
      t('bank_name_of_claimant_or_representative', 'Bank Name'),
      t('branch_number_of_claimant_or_representative_bank_account', 'Branch Number'),
      t('account_holder_name', 'Account Holder Name'),
      t('account_number_claimant', 'Account Number'),
    ],
  },
  {
    id: 'claimant',
    label: 'Claimant',
    shortLabel: 'Claim',
    fields: [
      t('title_cl', 'Title'),
      t('claimant_name_surname', 'Name & Surname'),
      t('claimant_name', 'Name'),
      t('claimant_id_or_passport_number', 'ID/Passport Number'),
      t('claimant_date_of_birth', 'Date of Birth', 'date'),
      t('claimant_address', 'Address'),
      t('claimant_province_address', 'Province'),
      t('claimant_address_postal_code', 'Postal Code'),
      t('claimant_telphone_number', 'Telephone'),
      t('claimant_work_number', 'Work Number'),
      t('claimant_cellular_number', 'Cellular'),
      t('claimant_email_address', 'Email'),
      t('claimant_preferred_communication_email', 'Pref: Email', 'checkbox', 'pref_comm'),
      t('claimant_preferred_communication_sms', 'Pref: SMS', 'checkbox', 'pref_comm'),
      t('claimant_preferred_communication_post', 'Pref: Post', 'checkbox', 'pref_comm'),
      t('claimant_preferred_communication_tel_cell', 'Pref: Tel/Cell', 'checkbox', 'pref_comm'),
      t('claimant_home_language', 'Home Language'),
      t('claimant_race', 'Race'),
      t('claimant_birth_country', 'Country of Birth'),
      t('claimant_relationship_to_injured_or_deceased', 'Relationship to Injured/Deceased'),
      t('claimant_male', 'Male', 'checkbox', 'gender'),
      t('claimant_female', 'Female', 'checkbox', 'gender'),
      t('his_her', 'His/Her'),
      t('myself_son_daughter_husband_wife', 'Myself/Relation (Declaration)'),
      t('I_son_daughter_husband_wife', 'I/Relation (Declaration)'),
    ],
  },
  {
    id: 'itp',
    label: 'Injured Third Party',
    shortLabel: 'ITP',
    fields: [
      t('itp_title', 'Title'),
      t('itp_fullnames', 'Full Names'),
      t('itp_date_of_birth', 'Date of Birth', 'date'),
      t('itp_id_or_passport_number', 'ID/Passport Number'),
      t('itp_id_passport', 'ID/Passport'),
      t('itp_identity_document_attached', 'ID Document Attached', 'checkbox'),
      t('itp_residential_address_complex', 'Residential: Complex'),
      t('itp_residential_address_street_name', 'Residential: Street'),
      t('itp_residential_address_town', 'Residential: Town'),
      t('itp_residential_address_province', 'Residential: Province'),
      t('itp_residential_address_postal_code', 'Residential: Postal Code'),
      t('itp_postal_address_complex', 'Postal: Complex'),
      t('itp_postal_address_street', 'Postal: Street'),
      t('itp_postal_address_town', 'Postal: Town'),
      t('itp_postal_address_province', 'Postal: Province'),
      t('itp_postal_address_postal_code', 'Postal: Postal Code'),
      t('itp_telephone_number', 'Telephone'),
      t('itp_work_number', 'Work Number'),
      t('itp_cellular_number', 'Cellular'),
      t('itp_email', 'Email'),
      t('itp_preferred_communication_email', 'Pref: Email', 'checkbox', 'pref_comm'),
      t('itp_preferred_communication_sms', 'Pref: SMS', 'checkbox', 'pref_comm'),
      t('itp_preferred_communication_post', 'Pref: Post', 'checkbox', 'pref_comm'),
      t('itp_preferred_communication_tel_cell', 'Pref: Tel/Cell', 'checkbox', 'pref_comm'),
      t('itp_home_lang', 'Home Language'),
      t('itp_marital_status', 'Marital Status'),
      t('itp_race', 'Race'),
      t('itp_country_of_birth', 'Country of Birth'),
      t('itp_country_of_residence', 'Country of Residence'),
      t('itp_male', 'Male', 'checkbox', 'gender'),
      t('itp_female', 'Female', 'checkbox', 'gender'),
    ],
  },
  {
    id: 'dtp',
    label: 'Deceased Third Party',
    shortLabel: 'DTP',
    fields: [
      t('dtp_title', 'Title'),
      t('dtp_fullnames', 'Full Names'),
      t('dtp_birth_date', 'Date of Birth', 'date'),
      t('dtp_date_of_death', 'Date of Death', 'date'),
      t('dtp_death_certificate_attached', 'Death Certificate Attached', 'checkbox'),
      t('dtp_id_number', 'ID Number'),
      t('dtp_passport_number', 'Passport Number'),
      t('dtp_identity_document_attached', 'ID Document Attached', 'checkbox'),
      t('dtp_residential_address_complex', 'Residential: Complex'),
      t('dtp_residential_address_street_name', 'Residential: Street'),
      t('dtp_residential_address_town', 'Residential: Town'),
      t('dtp_residential_address_province', 'Residential: Province'),
      t('dtp_residential_address_postal_code', 'Residential: Postal Code'),
      t('dtp_time_of_death', 'Time of Death'),
      t('dtp_country_of_birth', 'Country of Birth'),
      t('dtp_country_of_residence', 'Country of Residence'),
      t('dtp_male', 'Male', 'checkbox', 'gender'),
      t('dtp_female', 'Female', 'checkbox', 'gender'),
      t('dtp_injuries_if_dtp_did_not_die_at_accident', 'Injuries (if not died at scene)', 'checkbox'),
      t('dtp_injuries_severity_if_dtp_did_not_die_at_accident', 'Injury Severity'),
      t('dtp_injuries_list_if_dtp_did_not_die_at_accident', 'Injury List'),
      t('dtp_hospital_name_if_dtp_did_not_die_at_accident', 'Hospital Name'),
      t('dtp_hospital_address_if_dtp_did_not_die_at_accident', 'Hospital Address'),
      t('dtp_treating_practitioners_if_dtp_did_not_die_at_accident', 'Treating Practitioners'),
    ],
  },
  {
    id: 'dtp_employment',
    label: 'DTP Employment',
    shortLabel: 'DTP Emp',
    fields: [
      t('dtp_employment_status_employed', 'Employed', 'checkbox', 'employ_status'),
      t('dtp_employment_status_self_employed', 'Self-Employed', 'checkbox', 'employ_status'),
      t('dtp_employment_status_unemployed', 'Unemployed', 'checkbox', 'employ_status'),
      t('dtp_self_employed', 'Self-Employed', 'checkbox'),
      t('dtp_public_servant', 'Public Servant', 'checkbox'),
      t('dtp_regualted_industry', 'Regulated Industry', 'checkbox', 'industry_reg'),
      t('dtp_unregualted_industry', 'Unregulated Industry', 'checkbox', 'industry_reg'),
      t('dtp_occupation', 'Occupation'),
      t('dtp_annual_remuneration', 'Annual Remuneration', 'currency'),
      t('dtp_highest_qualification_ngf_level', 'Highest Qualification (NQF)'),
      t('dtp_employer_name', 'Employer Name'),
      t('dtp_employer_address', 'Employer Address'),
      t('dtp_employer_tel_no', 'Employer Telephone'),
      t('dtp_employer_contact_p', 'Employer Contact Person'),
      t('dtp_employee_number', 'Employee Number'),
      t('dtp_employment_period', 'Employment Period'),
      t('dtp_proof_of_income_other', 'Proof of Income'),
      t('dtp_tax_number', 'Tax Number'),
      t('dtp_self_emp_co_name', 'Self-Emp Company Name'),
      t('dtp_self_emp_co_nature', 'Nature of Company'),
      t('dtp_self_emp_co_address', 'Company Address'),
      t('dtp_permanent_employ', 'Permanent Employment', 'checkbox', 'employ_basis'),
      t('dtp_temp_employ', 'Temporary Employment', 'checkbox', 'employ_basis'),
      t('dtp_casual_contract', 'Casual/Contract', 'checkbox', 'employ_basis'),
      t('dtp_surviving_spouse_occupation', 'Surviving Spouse Occupation'),
      t('dtp_surviving_spouse_employer', 'Surviving Spouse Employer'),
      t('dtp_surviving_spouse_annual_remuneration', 'Surviving Spouse Remuneration', 'currency'),
      t('dtp_surviving_spouse_occupation_payslip', 'Surviving Spouse Payslip', 'checkbox'),
      t('dtp_surviving_spouse_tax_number', 'Surviving Spouse Tax Number'),
      t('dtp_surviving_spouse_declaration_to_give_consent', 'Surviving Spouse Consent', 'checkbox'),
    ],
  },
  {
    id: 'dependants',
    label: 'Dependants',
    shortLabel: 'Dep',
    fields: [
      ...[1, 2, 3, 4].flatMap((n) => [
        t(`dependant_${n}_title`, `Dependant ${n}: Title`),
        t(`dependant_${n}_fullnames`, `Dependant ${n}: Full Names`),
        t(`dependant_${n}_date_of_birth`, `Dependant ${n}: Date of Birth`, 'date'),
        t(`dependant_${n}_id_number_passport_number`, `Dependant ${n}: ID/Passport`),
        t(`dependant_${n}_race`, `Dependant ${n}: Race`),
        t(`dependant_${n}_country_of_birth`, `Dependant ${n}: Country of Birth`),
        t(`dependant_${n}_country_of_residence`, `Dependant ${n}: Country of Residence`),
        t(`dependant_${n}_gender`, `Dependant ${n}: Gender`),
        t(`dependant_${n}_relationship_to_claimant`, `Dependant ${n}: Relationship to Claimant`),
        t(`dependant_${n}_reason_for_dependence`, `Dependant ${n}: Reason for Dependence`),
        t(`dependant_${n}_marital_status`, `Dependant ${n}: Marital Status`),
      ]),
      t('dependant_5_fullnames', 'Dependant 5: Full Names'),
      t('dependant_5_date_of_birth', 'Dependant 5: Date of Birth', 'date'),
      t('dependant_5_id_number_passport_number', 'Dependant 5: ID/Passport'),
      t('dependant_5_relationship_to_claimant', 'Dependant 5: Relationship'),
      t('dependant_5_reason_for_dependence', 'Dependant 5: Reason for Dependence'),
    ],
  },
  {
    id: 'next_of_kin',
    label: 'Next of Kin',
    shortLabel: 'NoK',
    fields: [
      t('next_of_kin_title', 'Title'),
      t('next_of_kin_fullnames', 'Full Names'),
      t('next_of_kin_tel_no', 'Telephone'),
      t('next_of_kin_work_no', 'Work Number'),
      t('next_of_kin_cel_no', 'Cellular'),
      t('next_of_kin_tel_email', 'Email'),
      t('next_of_kin_relationship_to_claimant_or_injured_person', 'Relationship'),
    ],
  },
  {
    id: 'accident',
    label: 'Accident Details',
    shortLabel: 'Accident',
    fields: [
      t('date_of_accident', 'Date of Accident', 'date'),
      t('time_of_accident', 'Time of Accident'),
      t('street_name_of_where_accident_happened', 'Street Name'),
      t('town_name_of_where_accident_happened', 'Town'),
      t('province_name_of_where_accident_happened', 'Province'),
      t('postal_code_of_where_accident_happened', 'Postal Code'),
      t('police_station_where_accident_reported', 'Police Station'),
      t('town_of_police_station', 'Police Station Town'),
      t('province_of_police_station', 'Police Station Province'),
      t('postal_code_of_police_station', 'Police Station Postal Code'),
      t('contact_details_of_police', 'Police Contact'),
      t('name_of_nvestigating_officer_police', 'Investigating Officer'),
      t('accident_report_number', 'Accident Report Number'),
      t('case_number_of_accident', 'Case Number'),
      t('post_mortem_results_of_deceased', 'Post Mortem Results'),
      t('accident_information_raf_form_3_reference_number', 'RAF Form 3 Reference'),
      t('public_transport_yes', 'Public Transport (Yes)', 'checkbox', 'public_transport'),
      t('public_transport_no', 'Public Transport (No)', 'checkbox', 'public_transport'),
      t('capacity_motorcyclist', 'Motorcyclist', 'checkbox', 'capacity'),
      t('capacity_passenger', 'Passenger', 'checkbox', 'capacity'),
      t('capacity_cyclist', 'Cyclist', 'checkbox', 'capacity'),
      t('capacity_driver', 'Driver', 'checkbox', 'capacity'),
      t('capacity_pedestrian', 'Pedestrian', 'checkbox', 'capacity'),
      t('date_of_treatment', 'Date of Treatment', 'date'),
    ],
  },
  {
    id: 'vehicles',
    label: 'Vehicles & Driver',
    shortLabel: 'Vehicle',
    fields: [
      t('vehicle_registration_number', 'Vehicle Reg Number'),
      t('vehicle_registration_number_2', 'Vehicle Reg Number (2)'),
      t('vehicle_driver_fullnames', 'Driver Full Names'),
      t('vehicle_make_model', 'Vehicle Make/Model'),
      t('vehicle_type', 'Vehicle Type'),
      t('second_vehicle_registration_number', 'Second Vehicle Reg'),
      t('driver_residential_address_complex', 'Driver Address: Complex'),
      t('driver_residential_address_street_name', 'Driver Address: Street'),
      t('driver_residential_address_town', 'Driver Address: Town'),
      t('driver_residential_address_province', 'Driver Address: Province'),
      t('driver_residential_address_postal_code', 'Driver Address: Postal Code'),
      t('driver_cellular_number', 'Driver Cellular'),
      t('drivers_licence_number', "Driver's Licence Number"),
      t('vehicle_owner_name_surname', 'Owner Name'),
      t('vehicle_owner_telephone_number', 'Owner Telephone'),
      t('vehicle_owner_cell_number', 'Owner Cellular'),
      t('vehicle_owner_complex_name', 'Owner Address: Complex'),
      t('vehicle_owner_street_address', 'Owner Address: Street'),
      t('vehicle_owner_address_town', 'Owner Address: Town'),
      t('vehicle_owner_address_province', 'Owner Address: Province'),
      t('vehicle_owner_address_postal_code', 'Owner Address: Postal Code'),
      t('vehicle_owner_category_license_restrictions', 'Licence Category/Restrictions'),
      t('vehicle_owner_license_date_of_issue', 'Licence Date of Issue', 'date'),
      t('vehicle_license_val_f', 'Licence Valid From', 'date'),
      t('vehicle_license_val_t', 'Licence Valid To', 'date'),
      t('vehicle_owner_insurance_details', 'Insurance Details'),
      t('vehicle_registration_number_of_all_vehicles_involved', 'All Vehicles Involved (Reg)'),
      t('vehicle_make_model_of_all_vehicles_involved', 'All Vehicles Involved (Make/Model)'),
      t('driver_contact_details_of_all_vehicles_involved', 'All Drivers Contact'),
    ],
  },
  {
    id: 'accident_circumstances',
    label: 'Accident Circumstances',
    shortLabel: 'Circum',
    fields: [
      t('section_a_crossing_road_with_poor_visibility_unobstructed', 'Crossing road (poor visibility)', 'checkbox'),
      t('section_a_crossing_road_at_robot_controlled_intersection', 'Crossing at robot intersection', 'checkbox'),
      t('section_a_crossing_in_front_behind_stationary_vehicle', 'Crossing in front/behind vehicle', 'checkbox'),
      t('section_a_crossing_highway', 'Crossing highway', 'checkbox'),
      t('section_a_running_cycling_across_road', 'Running/cycling across road', 'checkbox'),
      t('section_a_pedestrian_standing_on_centre_line_painted', 'Pedestrian on centre line', 'checkbox'),
      t('section_a_pedestrian_under_7_year', 'Pedestrian under 7', 'checkbox'),
      t('section_a_pedestrian_between_7_14_years', 'Pedestrian 7-14 years', 'checkbox'),
      t('section_a_head_on_collision', 'Head-on collision', 'checkbox'),
      t('section_a_rear_end_collision', 'Rear-end collision', 'checkbox'),
      t('section_a_stop_street_controlled_intersection_4_way', 'Stop street / 4-way', 'checkbox'),
      t('section_a_robot_controlled_intersection', 'Robot controlled intersection', 'checkbox'),
      t('section_a_tyre_burst', 'Tyre burst', 'checkbox'),
      t('section_a_collision_with_animal', 'Collision with animal', 'checkbox'),
      t('section_a_single_vehicle_accident', 'Single vehicle accident', 'checkbox'),
      t('section_a_accident_with_object', 'Accident with object', 'checkbox'),
      t('section_a_poor_visibility_dust_cloud_smoke', 'Poor visibility (dust/smoke)', 'checkbox'),
      t('section_a_right_turn', 'Right turn', 'checkbox'),
      t('section_a_overtaking', 'Overtaking', 'checkbox'),
      t('section_a_lane_change', 'Lane change', 'checkbox'),
      t('section_a_t_junction', 'T-junction', 'checkbox'),
      t('section_a_merging_joining_yield_sign', 'Merging/yield sign', 'checkbox'),
      t('section_a_traffic_circle', 'Traffic circle', 'checkbox'),
      t('section_a_stationary_vehicle', 'Stationary vehicle', 'checkbox'),
      t('section_a_reversing', 'Reversing', 'checkbox'),
    ],
  },
  {
    id: 'witnesses',
    label: 'Witnesses & Safety',
    shortLabel: 'Witness',
    fields: [
      t('u_yes', 'U (Yes)', 'checkbox', 'u'),
      t('u_n', 'U (No)', 'checkbox', 'u'),
      t('w_yes', 'W (Yes)', 'checkbox', 'w'),
      t('w_no', 'W (No)', 'checkbox', 'w'),
      t('witness_fullnames', 'Witness Full Names'),
      t('witness_address', 'Witness Address'),
      t('witness_telephone_number', 'Witness Telephone'),
      t('witness_cellular_number', 'Witness Cellular'),
      t('seatbelt_helmet_yes', 'Seatbelt/Helmet (Yes)', 'checkbox', 'seatbelt_helmet'),
      t('seatbelt_helmet_no', 'Seatbelt/Helmet (No)', 'checkbox', 'seatbelt_helmet'),
      t('blood_alcohol_tested_yes', 'Blood Alcohol Tested (Yes)', 'checkbox', 'blood_alcohol_tested'),
      t('blood_alcohol_tested_no', 'Blood Alcohol Tested (No)', 'checkbox', 'blood_alcohol_tested'),
      t('blood_alcohol_results', 'Blood Alcohol Results'),
      t('blood_alcohol_results_attached_yes', 'Results Attached (Yes)', 'checkbox', 'blood_alcohol_results_attached'),
      t('blood_alcohol_results_attached_no', 'Results Attached (No)', 'checkbox', 'blood_alcohol_results_attached'),
    ],
  },
  {
    id: 'employment',
    label: 'Employment (ITP)',
    shortLabel: 'Employ',
    fields: [
      t('employment_status_employed', 'Employed', 'checkbox', 'employ_status'),
      t('employment_status_self_employed', 'Self-Employed', 'checkbox', 'employ_status'),
      t('employment_status_unemployed', 'Unemployed', 'checkbox', 'employ_status'),
      t('self_employed', 'Self-Employed', 'checkbox'),
      t('public_servant', 'Public Servant', 'checkbox'),
      t('formal_regulated', 'Formal/Regulated', 'checkbox', 'industry_reg'),
      t('informal_inregulated', 'Informal/Unregulated', 'checkbox', 'industry_reg'),
      t('occupation', 'Occupation'),
      t('annual_remuneration_pre_post_accident', 'Annual Remuneration', 'currency'),
      t('highest_qualification_nqf_level', 'Highest Qualification (NQF)'),
      t('was_injured_required_take_time_duty', 'Time off Duty Required'),
      t('yes_please_specify_dates', 'Dates off Duty'),
      t('number_work_days_absent', 'Work Days Absent'),
      t('did_you_receive_remuneration_while_away', 'Remuneration While Away'),
      t('state_amount_received', 'Amount Received', 'currency'),
      t('nature_payment_received', 'Nature of Payment'),
      t('name_employer', 'Employer Name'),
      t('postal_address_employer', 'Employer Address'),
      t('telephone_number_employer', 'Employer Telephone'),
      t('contact_person_employer', 'Employer Contact Person'),
      t('employee_number', 'Employee Number'),
      t('basis_employment_permanent', 'Permanent', 'checkbox', 'employ_basis'),
      t('basis_employment_temporary', 'Temporary', 'checkbox', 'employ_basis'),
      t('basis_employment_casual_contract', 'Casual/Contract', 'checkbox', 'employ_basis'),
      t('period_temporary_contract_casual_employment', 'Period of Employment'),
      t('proof_income_payslips', 'Proof: Payslips', 'checkbox'),
      t('proof_income_tax_return', 'Proof: Tax Return', 'checkbox'),
      t('proof_income_raf_consent', 'Proof: RAF Consent', 'checkbox'),
      t('proof_income_printout_payments_from_employer', 'Proof: Employer Printout', 'checkbox'),
      t('proof_income_bank_statements', 'Proof: Bank Statements', 'checkbox'),
      t('proof_income_other_specify', 'Proof: Other'),
      t('proof_income_tax_reference_number', 'Tax Reference Number'),
      t('self_emp_company_name', 'Self-Emp Company Name'),
      t('self_emp_nature_of_company', 'Nature of Company'),
      t('self_emp_company_address', 'Company Address'),
      t('legal_entity_sole_trader', 'Sole Trader', 'checkbox', 'legal_entity'),
      t('legal_entity_partnership', 'Partnership', 'checkbox', 'legal_entity'),
      t('legal_entity_trust', 'Trust', 'checkbox', 'legal_entity'),
      t('legal_entity_company', 'Company', 'checkbox', 'legal_entity'),
      t('legal_entity_close_corporation', 'Close Corporation', 'checkbox', 'legal_entity'),
      t('legal_entity_other', 'Other', 'checkbox', 'legal_entity'),
      t('legal_entity_other_specify', 'Other (specify)'),
    ],
  },
  {
    id: 'injuries',
    label: 'Injuries & Medical',
    shortLabel: 'Injury',
    fields: [
      t('types_of_injuries', 'Types of Injuries'),
      t('severity_of_injuries', 'Severity'),
      t('list_of_injuries', 'List of Injuries'),
      t('hospital_names_where_injuries_were_treated', 'Hospital Names'),
      t('hospital_address_where_injuries_were_treated', 'Hospital Address'),
      t('medical_practiioners_names_who_treated_injuries', 'Medical Practitioners'),
    ],
  },
  {
    id: 'minors',
    label: 'Minors',
    shortLabel: 'Minor',
    fields: [
      t('minor_education_level_at_time_of_accident', 'Education at Accident'),
      t('minor_age_at_time_of_accident', 'Age at Accident'),
      t('minor_education_level_at_time_of_submitting_claim', 'Education at Claim'),
      t('minor_age_at_time_of_submitting_claim', 'Age at Claim'),
      t('minor_pre_accident_report_attached', 'Pre-Accident Report', 'checkbox'),
      t('minor_post_accident_report_attached', 'Post-Accident Report', 'checkbox'),
    ],
  },
  {
    id: 'amounts',
    label: 'Amounts Claimed',
    shortLabel: 'Amounts',
    fields: [
      t('amount_emergency_medical', 'Emergency Medical', 'currency'),
      t('amount_non_emergency_medical', 'Non-Emergency Medical', 'currency'),
      t('amount_future_medical', 'Future Medical', 'currency'),
      t('amount_past_loss_income', 'Past Loss of Income', 'currency'),
      t('amount_future_loss_income', 'Future Loss of Income', 'currency'),
      t('amount_past_loss_support', 'Past Loss of Support', 'currency'),
      t('amount_future_loss_support', 'Future Loss of Support', 'currency'),
      t('amount_funeral_expenses', 'Funeral Expenses', 'currency'),
      t('amount_total_claimed', 'Total Claimed', 'currency'),
    ],
  },
  {
    id: 'workers_comp',
    label: "Workers' Comp",
    shortLabel: 'WC',
    fields: [
      t('wc_mva_y', 'WC MVA (Yes)', 'checkbox', 'wc_mva'),
      t('wc_mva_n', 'WC MVA (No)', 'checkbox', 'wc_mva'),
      t('wc_claim_y', 'WC Claim (Yes)', 'checkbox'),
      t('wc_fund_reference_no', 'WC Fund Reference'),
      t('wc_fund_amount', 'WC Fund Amount', 'currency'),
      t('wc_award_y', 'WC Award (Yes)', 'checkbox', 'wc_award'),
      t('wc_award_n', 'WC Award (No)', 'checkbox', 'wc_award'),
      t('dtp_wc_mva_y', 'DTP WC MVA (Yes)', 'checkbox'),
      t('dtp_wc_fund_ref_no', 'DTP WC Fund Reference'),
      t('dtp_wc_fund_amount', 'DTP WC Fund Amount', 'currency'),
      t('dtp_wc_award_y', 'DTP WC Award (Yes)', 'checkbox', 'dtp_wc_award'),
      t('dtp_wc_award_n', 'DTP WC Award (No)', 'checkbox', 'dtp_wc_award'),
    ],
  },
  {
    id: 'declaration',
    label: 'Declaration',
    shortLabel: 'Decl',
    fields: [
      t('day', 'Day'),
      t('month', 'Month'),
      t('year', 'Year'),
    ],
  },
];

const fieldHasValue = (v: unknown): boolean => {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return !Number.isNaN(v);
  return true;
};

/**
 * Count completion for a section, treating fields that share a `groupId` as a
 * single logical field (e.g. mutually-exclusive Yes/No checkbox pairs). Returns
 * `{ filled, total, pct }` where `total` is the count of logical fields
 * (groups + ungrouped fields) and `filled` counts a group as 1 if any of its
 * member fields has a value.
 *
 * When `isNa` is true, the section is treated as zero-filled but the total
 * still reflects the underlying logical-field count so the denominator stays
 * stable when toggling N/A.
 */
export function computeSectionCompletion(
  section: FieldSection,
  data: Record<string, unknown> | undefined,
  isNa = false,
): { filled: number; total: number; pct: number } {
  const seenGroups = new Set<string>();
  const filledGroups = new Set<string>();
  let total = 0;
  let filled = 0;
  for (const field of section.fields) {
    const isFilled = fieldHasValue(data?.[field.key]);
    if (field.groupId) {
      if (!seenGroups.has(field.groupId)) {
        seenGroups.add(field.groupId);
        total += 1;
      }
      if (isFilled) filledGroups.add(field.groupId);
    } else {
      total += 1;
      if (isFilled) filled += 1;
    }
  }
  if (!isNa) filled += filledGroups.size;
  else filled = 0;
  const pct = total === 0 ? 0 : Math.round((filled / total) * 100);
  return { filled, total, pct };
}
